import { useState, useEffect, useCallback } from "react";
import "../styles/chat.css";
import Header from "../components/Layout/Header";
import Sidebar from "../components/Layout/Sidebar";
import ChatWindow from "../components/Chat/ChatWindow";
import api from "../api/axios";

const STORAGE_KEYS = {
  CHATS: "fitnova_chats",
  ACTIVE_CHAT: "fitnova_active_chat",
  TITLES: "fitnova_chat_titles",
};

const loadFromStorage = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

const saveToStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error("Failed to save to localStorage:", error);
  }
};

const makeLocalId = () => `chat_${Date.now()}`;

function ChatPage() {
  const [chats, setChats] = useState({});
  const [chatTitles, setChatTitles] = useState({});
  const [activeChatId, setActiveChatId] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loadedSessionIds, setLoadedSessionIds] = useState({});

  const ensureEmptyChat = useCallback((prevChats, prevTitles) => {
    const newId = makeLocalId();
    return {
      chats: { ...(prevChats || {}), [newId]: [] },
      titles: { ...(prevTitles || {}), [newId]: "New Chat" },
      activeId: newId,
    };
  }, []);

  // Load server history on mount; always open a fresh New Chat on refresh
  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setHistoryLoading(true);
      try {
        const res = await api.get("chat/sessions/");
        const sessions = res.data?.sessions || [];

        if (cancelled) return;

        const nextChats = {};
        const nextTitles = {};
        sessions.forEach((s) => {
          nextChats[s.session_id] = []; // load messages on select
          nextTitles[s.session_id] = s.title || "New Chat";
        });

        // Always start with a blank New Chat after refresh / reopen
        const fresh = ensureEmptyChat(nextChats, nextTitles);
        setChats(fresh.chats);
        setChatTitles(fresh.titles);
        setActiveChatId(fresh.activeId);
        setLoadedSessionIds({});
        setSidebarCollapsed(false);
      } catch (err) {
        console.error("Failed to load chat history:", err);
        if (cancelled) return;
        const savedChats = loadFromStorage(STORAGE_KEYS.CHATS, {});
        const savedTitles = loadFromStorage(STORAGE_KEYS.TITLES, {});
        // Drop empty local draft chats; keep only real session history if present
        const cleanedChats = {};
        const cleanedTitles = {};
        Object.keys(savedChats || {}).forEach((id) => {
          if (!id.startsWith("chat_")) {
            cleanedChats[id] = savedChats[id];
            cleanedTitles[id] = savedTitles[id] || "New Chat";
          }
        });
        const fresh = ensureEmptyChat(cleanedChats, cleanedTitles);
        setChats(fresh.chats);
        setChatTitles(fresh.titles);
        setActiveChatId(fresh.activeId);
        setSidebarCollapsed(false);
      } finally {
        if (!cancelled) setHistoryLoading(false);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [ensureEmptyChat]);

  // Persist cache for offline fallback (server is source of truth when online)
  useEffect(() => {
    if (!historyLoading && Object.keys(chats).length > 0) {
      saveToStorage(STORAGE_KEYS.CHATS, chats);
      saveToStorage(STORAGE_KEYS.TITLES, chatTitles);
    }
  }, [chats, chatTitles, historyLoading]);

  useEffect(() => {
    if (activeChatId) {
      saveToStorage(STORAGE_KEYS.ACTIVE_CHAT, activeChatId);
    }
  }, [activeChatId]);

  // Load messages when selecting a server session
  useEffect(() => {
    if (!activeChatId || activeChatId.startsWith("chat_")) return;
    if (loadedSessionIds[activeChatId]) return;
    if ((chats[activeChatId] || []).length > 0) {
      setLoadedSessionIds((prev) => ({ ...prev, [activeChatId]: true }));
      return;
    }

    let cancelled = false;
    const fetchMessages = async () => {
      try {
        const res = await api.get(`chat/sessions/${activeChatId}/`);
        if (cancelled) return;
        const msgs = (res.data?.messages || []).map((m) => ({
          from: m.from,
          text: m.text,
        }));
        setChats((prev) => ({ ...prev, [activeChatId]: msgs }));
        if (res.data?.title) {
          setChatTitles((prev) => ({ ...prev, [activeChatId]: res.data.title }));
        }
        setLoadedSessionIds((prev) => ({ ...prev, [activeChatId]: true }));
      } catch (err) {
        console.error("Failed to load chat messages:", err);
        setLoadedSessionIds((prev) => ({ ...prev, [activeChatId]: true }));
      }
    };
    fetchMessages();
    return () => {
      cancelled = true;
    };
  }, [activeChatId, chats, loadedSessionIds]);

  useEffect(() => {
    const chatIds = Object.keys(chats);
    if (!historyLoading && chatIds.length === 0) {
      const empty = ensureEmptyChat({}, {});
      setChats(empty.chats);
      setChatTitles(empty.titles);
      setActiveChatId(empty.activeId);
    } else if (activeChatId && chatIds.length > 0 && !chats[activeChatId]) {
      setActiveChatId(chatIds[0]);
    }
  }, [activeChatId, chats, historyLoading, ensureEmptyChat]);

  const activeMessages = chats[activeChatId] || [];
  const hasMessages = activeMessages.length > 0;

  useEffect(() => {
    if (hasMessages) setSidebarCollapsed(true);
  }, [hasMessages]);

  const handleSelectChat = (chatId) => {
    if (chats[chatId] !== undefined) {
      setActiveChatId(chatId);
    }
  };

  const handleNewChat = () => {
    const newId = makeLocalId();
    setChats((prev) => ({ ...prev, [newId]: [] }));
    setChatTitles((prev) => ({ ...prev, [newId]: "New Chat" }));
    setActiveChatId(newId);
  };

  const handleDeleteChat = async (chatId) => {
    const chatIds = Object.keys(chats);

    if (!chatId.startsWith("chat_")) {
      try {
        await api.delete(`chat/sessions/${chatId}/delete/`);
      } catch (err) {
        console.error("Failed to delete chat on server:", err);
      }
    }

    if (chatIds.length <= 1) {
      const newId = makeLocalId();
      setChats({ [newId]: [] });
      setChatTitles({ [newId]: "New Chat" });
      setActiveChatId(newId);
      return;
    }

    setChats((prev) => {
      const { [chatId]: _removed, ...rest } = prev;
      return rest;
    });
    setChatTitles((prev) => {
      const { [chatId]: _removed, ...rest } = prev;
      return rest;
    });

    if (activeChatId === chatId) {
      const remainingIds = chatIds.filter((id) => id !== chatId);
      if (remainingIds.length > 0) {
        setActiveChatId(remainingIds[0]);
      }
    }
  };

  const updateMessages = (newMessages, newSessionId = null, title = null) => {
    if (!activeChatId) return;

    if (newSessionId && activeChatId.startsWith("chat_")) {
      setChats((prev) => {
        const { [activeChatId]: _old, ...rest } = prev;
        return { ...rest, [newSessionId]: newMessages };
      });
      setChatTitles((prev) => {
        const { [activeChatId]: oldTitle, ...rest } = prev;
        return {
          ...rest,
          [newSessionId]: title || oldTitle || "New Chat",
        };
      });
      setLoadedSessionIds((prev) => ({ ...prev, [newSessionId]: true }));
      setActiveChatId(newSessionId);
    } else {
      const key = newSessionId || activeChatId;
      setChats((prev) => ({ ...prev, [key]: newMessages }));
      if (title) {
        setChatTitles((prev) => ({ ...prev, [key]: title }));
      }
    }
  };

  if (historyLoading) {
    return (
      <div className="app-container d-flex align-items-center justify-content-center" style={{ minHeight: "100vh" }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status" />
          <p className="text-muted mt-3 mb-0">Loading chat history…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <div className="main-container">
        <Sidebar
          chats={chats}
          chatTitles={chatTitles}
          activeChatId={activeChatId}
          onSelectChat={handleSelectChat}
          onDeleteChat={handleDeleteChat}
          onChatHistoryClick={() => setSidebarCollapsed((prev) => !prev)}
          onCloseSidebar={() => setSidebarOpen(false)}
          collapsed={sidebarCollapsed}
          isOpen={sidebarOpen}
        />

        <div className="chat-main">
          <Header
            onNewChat={handleNewChat}
            sidebarOpen={sidebarOpen}
            onToggleSidebar={() => setSidebarOpen((prev) => !prev)}
          />
          <ChatWindow
            messages={activeMessages}
            setMessages={updateMessages}
            sessionId={activeChatId?.startsWith("chat_") ? null : activeChatId}
          />
        </div>
      </div>
    </div>
  );
}

export default ChatPage;
